'use client';

import { useBunja } from 'bunja/react';
import { useAtomValue, useSetAtom } from 'jotai';
import { useEffect } from 'react';

import { FormattedMessage } from '@flipster/i18n';
import { Modal } from '@flipster/react/design-system/modal';
import { css } from '@flipster/styled-system/css';
import { stack } from '@flipster/styled-system/patterns';
import segment from '@flipster/utils/segment';
import { FeatureGimmickEvent } from '@flipster/utils/segment/events/feature-gimmick';

import { featureHighlightBunja } from './bunja';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onExit: () => void;
}

export const FeatureHighlightPopup = ({ isOpen, onClose, onExit }: Props) => {
  const { featureHighlightAtom, featureHighlightVersionAtom } = useBunja(featureHighlightBunja);
  const featureHighlight = useAtomValue(featureHighlightAtom);
  const setFeatureHighlightVersion = useSetAtom(featureHighlightVersionAtom);

  const handleCloseModal = () => {
    if (featureHighlight && featureHighlight.version !== undefined) {
      setFeatureHighlightVersion(String(featureHighlight.version));
    }
    segment.track(FeatureGimmickEvent.FeatureIntroModalClose, {
      title: featureHighlight?.highlight?.title ?? '',
      version_id: featureHighlight?.version?.toString() ?? '',
    });
    onClose();
  };

  const handleCtaClick = () => {
    segment.track(FeatureGimmickEvent.FeatureIntroModalCtaClick, {
      title: featureHighlight?.highlight?.title ?? '',
      version_id: featureHighlight?.version?.toString() ?? '',
    });
    handleCloseModal();
  };

  const handleSeeMoreClick = () => {
    segment.track(FeatureGimmickEvent.FeatureIntroModalSeeMoreClick, {
      title: featureHighlight?.highlight?.title ?? '',
      version_id: featureHighlight?.version?.toString() ?? '',
    });
    handleCloseModal();
  };
  // Track modal view
  useEffect(() => {
    if (!featureHighlight) return;
    segment.track(FeatureGimmickEvent.FeatureIntroModalView, {
      title: featureHighlight?.highlight?.title ?? '',
      version_id: featureHighlight?.version?.toString() ?? '',
    });
  }, [featureHighlight]);

  if (!featureHighlight) return null;
  const { highlight, hasMoreFeatures } = featureHighlight;
  const { title, description, imageUrl, buttonText, pageLink } = highlight ?? {};
  if (!title || !description || !imageUrl || !buttonText || !pageLink) return null;

  return (
    <Modal.Root
      isOpen={isOpen}
      onClose={() => {
        segment.track(FeatureGimmickEvent.FeatureIntroModalClose, {
          title: featureHighlight?.highlight?.title ?? '',
          version_id: featureHighlight?.highlight?.buttonText ?? '',
        });
        handleCloseModal();
      }}
      onExit={onExit}
    >
      <Modal.Header>
        <Modal.Title label={title} />
      </Modal.Header>
      <Modal.Content>
        <div className={stack({ gap: 24 })}>
          <p
            className={css({
              textStyle: 'Text/L/Regular',
              color: 'content.default.level-3',
              whiteSpace: 'pre-line',
            })}
          >
            {description}
          </p>
          {imageUrl && (
            <div
              className={css({
                w: '100%',
                h: '100%',
                rounded: 8,
                aspectRatio: '1.8 / 1',
                overflow: 'hidden',
              })}
            >
              <img
                src={imageUrl}
                alt={title}
                className={css({
                  w: '100%',
                  h: '100%',
                  objectFit: 'contain',
                })}
              />
            </div>
          )}
        </div>
      </Modal.Content>

      <Modal.Footer align='vertical'>
        {hasMoreFeatures && (
          <Modal.Button
            size='large'
            hierarchy='secondary'
            color='neutral'
            label={<FormattedMessage id='Common.r251203.See_more_updates' />}
            as='a'
            href='/feature-updates'
            onClick={handleSeeMoreClick}
          />
        )}
        <Modal.Button
          size='large'
          hierarchy='primary'
          label={buttonText}
          onClick={handleCtaClick}
          as='a'
          href={pageLink}
        />
      </Modal.Footer>
    </Modal.Root>
  );
};
